import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import {
  Button as RaButton,
  useCreate,
  useListContext,
  useNotify,
  useTranslate,
} from 'react-admin';
import { getCurrentUserId } from '../../../../helpers/auth';
import { handleError } from '../../../../helpers/error';
import DownloadIcon from '@material-ui/icons/GetApp';
import useConfig from '../../../../hooks/useConfig';
import ModalExport from './modalExport';

type ExportChoice = 'filtered' | 'all';

const ExportButton: FunctionComponent<{ resourceName?: string }> = ({
  resourceName: propResourceName,
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const listContext = useListContext();
  const config = useConfig();

  const resourceName = propResourceName ?? listContext?.resource;
  const filterValues = listContext?.filterValues ?? {};

  const resourceConfig = config.getResource(resourceName);
  const resourceLabel = resourceConfig ? t(resourceConfig.label) : resourceName;
  const kindType = resourceConfig?.getExportKindType();
  const [open, setOpen] = useState(false);
  const EXPORT_MODAL_RESOURCES = new Set([
    'candidacies',
    'cgus',
    'client_invoices',
    'clients',
    'contract_milestones',
    'contracts',
    'credit_notes',
    'document_files',
    'documents',
    'expert_invoices',
    'experts',
    'invitations',
    'invoices',
    'legal_informations',
    'missions',
    'partner_missions',
    'proposals',
    'purchase_orders',
    'recommendations',
    'validation_orders',
  ]);
  const [create] = useCreate(
    `${resourceName}/export`,
    {},
    {
      onSuccess: () => {
        notify('notify.success.export');
        setOpen(false);
      },
      onFailure: (error: Record<string, any>) =>
        handleError(error, notify, 'notify.error.export', t),
    }
  );

  const handleExport = (choice?: ExportChoice) => {
    const payload: Record<string, any> = {
      admin_id: getCurrentUserId(),
      kind: kindType,
    };

    if (choice === 'filtered' && Object.keys(filterValues).length > 0) {
      payload.filters = filterValues;
    }

    create({ payload: { data: payload } });
  };

  const handleClick = () => {
    const hasFilters = Object.keys(filterValues).length > 0;
    if (EXPORT_MODAL_RESOURCES.has(resourceName) && hasFilters) {
      setOpen(true);
    } else {
      handleExport(hasFilters ? 'filtered' : 'all');
    }
  };

  return (
    <>
      <RaButton onClick={handleClick} label={t('resource.action.export')}>
        <DownloadIcon />
      </RaButton>

      {EXPORT_MODAL_RESOURCES.has(resourceName) && (
        <ModalExport
          open={open}
          onClose={() => setOpen(false)}
          onConfirm={handleExport}
          resourceLabel={resourceLabel}
        />
      )}
    </>
  );
};

export default ExportButton;
